import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { ApiError, errorMessage, NetworkError } from '@/api/errors'
import * as session from '@/auth/session-store'
import { config } from '@/config'
import { notify } from '@/lib/toast'
import * as org from '@/org/org-store'
import { routeTree } from '@/routeTree.gen'

import './styles.css'

// ── query client ──────────────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Un 4xx non migliora riprovando: è la richiesta a essere sbagliata.
        // Un 429 si riprova, ma solo dopo `Retry-After`, e quello lo gestisce il
        // chiamante — qui si rinuncia per non moltiplicare le richieste bloccate.
        if (error instanceof ApiError) return false
        // Un errore di rete può essere un blip: due tentativi, non di più.
        return error instanceof NetworkError && failureCount < 2
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // `mfa_setup_required` è l'unico errore che vale un toast **sempre**, anche
      // sul primo caricamento: con `mfa.enforceForAdmins` attivo lo restituisce
      // l'intero perimetro di piattaforma, quindi ogni pagina fallirebbe allo
      // stesso modo e l'operatore vedrebbe un pannello rotto senza sapere che la
      // cura è a due click di distanza (OPS-04).
      if (error instanceof ApiError && error.isMfaSetupRequired) {
        notify({
          message: `Serve la verifica in due passaggi. ${error.message} Aprila da Impostazioni.`,
          variant: 'danger',
          // La chiave, non un id qualunque: lo stesso errore arriva da ogni
          // query del perimetro, e senza deduplicazione sarebbero sei toast
          // identici impilati al primo caricamento. Vedi `Notification.key`.
          key: 'mfa-setup-required',
        })
        return
      }

      // Le query mostrano l'errore in pagina. Il toast serve solo per i refetch in
      // background di dati già a schermo, dove altrimenti l'errore passerebbe muto —
      // e in quel caso la pagina continua a mostrare i dati precedenti, quindi il
      // toast è l'**unico** segnale (vedi la nota su `keepVisible` nelle pagine).
      if (query.state.data !== undefined) {
        notify({
          message: `Aggiornamento non riuscito. ${errorMessage(error)}`,
          variant: 'danger',
        })
      }
    },
  }),
  // Nessun `onError` globale sulle mutazioni: ognuna gestisce il proprio errore in
  // modo specifico (errori per campo, conflitti, conferme) e mostra già un toast.
  // Un handler globale ne aggiungerebbe un secondo sopra il primo.
  mutationCache: new MutationCache(),
})

// ── router ────────────────────────────────────────────────────────────────────

const router = createRouter({
  routeTree,
  context: { queryClient },
  // Prefisso sotto cui il pannello è servito, per far coincidere le URL generate
  // dal router con quelle che il server web risolve davvero.
  //
  // Viene da `base` di Vite (vedi vite.config.ts), non da una costante propria:
  // così esiste una sola verità. Vale '/' in sviluppo e '/admin/' quando il
  // pannello sta sotto un path di un hostname condiviso — che è il caso finché
  // non c'è un sottodominio dedicato.
  //
  // Senza questo il router genererebbe `/utenti/123` invece di
  // `/admin/utenti/123`: la navigazione interna sembrerebbe funzionare — la fa
  // lui in memoria — ma ogni refresh e ogni link copiato finirebbero fuori dal
  // pannello.
  basepath: import.meta.env.BASE_URL,
  defaultPreload: 'intent',
  // Il preload sui link visitati di recente non deve rileggere: la cache di
  // TanStack Query è già l'autorità su quanto un dato è fresco.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

/**
 * Fine della sessione, per entrambe le strade: l'uscita voluta e la perdita
 * (refresh scaduto o riusato, account disabilitato, ruolo revocato).
 *
 * Le due cose da fare sono le stesse, e vanno fatte in tutti e due i casi:
 *
 * 1. **svuotare la cache**, non invalidarla: contiene email, profili e schede di
 *    utenti reali, letti con privilegi admin. Invalidarla la lascerebbe in memoria e
 *    la renderebbe riusabile dal login successivo nella stessa tab;
 * 2. **uscire dalla pagina autenticata.** La guardia di `_app` vive in `beforeLoad`,
 *    che gira solo al matching di una rotta: senza una navigazione esplicita il DOM
 *    resterebbe montato, con i dati della persona ancora a schermo.
 *
 * Cambia solo il messaggio, e la destinazione: da un'uscita voluta si va al login
 * pulito, da una sessione persa si conserva la pagina per tornarci dopo.
 */
session.setOnSessionEnded((reason) => {
  queryClient.clear()
  // L'organizzazione scelta appartiene a **quell'** account: lasciarla in
  // `localStorage` la farebbe applicare al login successivo su questa macchina,
  // che con un account diverso significa un `403` a ogni richiesta.
  org.clearOrganization()

  if (reason.kind === 'lost') {
    notify({ message: `Sessione terminata. ${reason.error.message}`, variant: 'danger' })
    void router.navigate({
      to: '/login',
      search: { redirect: router.state.location.href },
      replace: true,
    })
    return
  }

  void router.navigate({ to: '/login', replace: true })
})

/**
 * Cambio di organizzazione — TEN-06.
 *
 * `clear()` e non `invalidateQueries()`, per la stessa ragione per cui la fine
 * della sessione svuota: le chiavi di query **non** contengono l'id
 * dell'organizzazione, quindi una semplice invalidazione lascerebbe in cache gli
 * elenchi della palestra precedente e `placeholderData: (previous) => previous`
 * li rimostrerebbe — sotto il nome di quella nuova, senza alcun segnale che siano
 * di un'altra. Fra "una schermata di caricamento in più" e "dati giusti sotto
 * l'intestazione sbagliata", si sceglie il caricamento.
 *
 * Nessuna navigazione: cambiare palestra non è uscire dal pannello, e la pagina
 * su cui si sta lavorando resta quella giusta — con i dati dell'organizzazione
 * nuova.
 */
org.setOnOrganizationChanged((change) => {
  queryClient.clear()

  if (change.kind === 'rejected') {
    notify({
      message: `Organizzazione non più disponibile. ${change.error.message} Si riparte dall'organizzazione predefinita.`,
      variant: 'danger',
    })
  }
})

// ── avvio ─────────────────────────────────────────────────────────────────────

async function bootstrap() {
  // `import.meta.env.DEV` va letto **qui**, non tramite `config`: Vite lo sostituisce
  // con `false` in build di produzione, l'intero blocco diventa codice morto e MSW
  // (440 kB) non entra nel bundle. Dietro un flag calcolato a runtime il bundler non
  // può dimostrarlo e il mock finirebbe in produzione.
  if (import.meta.env.DEV && config.enableMocks) {
    try {
      const { startMocks } = await import('@/mocks/browser')
      await startMocks()
    } catch (error) {
      // La registrazione del service worker può fallire per motivi transitori — un
      // controllo di aggiornamento che cade mentre un'altra tab sta registrando lo
      // stesso worker. Non è un motivo per non montare l'applicazione: senza mock le
      // chiamate vanno al backend reale, ed è meglio di una pagina bianca.
      console.error('MSW non è partito: le chiamate andranno al backend reale.', error)
    }
  }

  // Si attende l'esito del refresh silenzioso **prima** di montare il router: così
  // le guardie di rotta vedono uno stato già deciso e non serve gestire un terzo
  // stato "sto ancora capendo se sei autenticato".
  await session.restoreSession()

  const container = document.getElementById('root')
  if (!container) throw new Error('Elemento #root assente in index.html')

  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  )
}

/**
 * Un avvio fallito non deve lasciare una pagina bianca.
 *
 * `void bootstrap()` trasformerebbe qualsiasi errore in una rejection non gestita: la
 * console mostra lo stack e lo schermo resta vuoto, senza niente che dica cosa fare.
 * Qui il fallimento diventa un messaggio leggibile, in italiano come il resto.
 */
bootstrap().catch((error: unknown) => {
  console.error('Avvio del pannello non riuscito', error)

  const container = document.getElementById('root')
  if (!container) return

  const detail = error instanceof Error ? error.message : String(error)
  container.replaceChildren()

  const box = document.createElement('div')
  box.setAttribute(
    'style',
    'min-height:100dvh;display:flex;align-items:center;justify-content:center;padding:1.5rem;font-family:ui-sans-serif,system-ui,sans-serif',
  )
  box.innerHTML = `
    <div style="max-width:32rem;text-align:center">
      <h1 style="font-size:1.125rem;font-weight:600;margin:0 0 .5rem">
        Il pannello non è riuscito ad avviarsi
      </h1>
      <p style="opacity:.7;margin:0 0 1rem;font-size:.875rem">
        Ricarica la pagina. Se il problema resta, il backend potrebbe non essere
        raggiungibile.
      </p>
      <pre style="text-align:left;overflow:auto;padding:.75rem;border-radius:.75rem;
                  background:rgba(127,127,127,.12);font-size:.75rem;margin:0"></pre>
    </div>`
  // Il dettaglio va inserito come testo, non come HTML: può contenere input non fidato.
  const pre = box.querySelector('pre')
  if (pre) pre.textContent = detail
  container.append(box)
})
